import { EntityRepository } from '@/interfaces'
import model from './model'
import type ObjectInterface from './interface'
import { hydrateEntityObject } from '@/entities/lib'
import graphQLClient from '@/lib/api/graphQLClient'
import { TFunction } from 'next-i18next'
import { useMemo } from 'react'
import useSWR from 'swr'
import { uppercaseFirst } from '@/lib/helpers/helpers'
import travelPricingAgeGroupObject from '@/entities/travelPricingAgeGroup/interface'

import {
  ALL_AGE_GROUPED_TOTAL_CAPACITIES_QUERY,
  SINGLE_AGE_GROUPED_TOTAL_CAPACITY_QUERY,
  AGE_GROUPED_TOTAL_CAPACITIES_DEPENDENTS_QUERY,
  CREATE_AGE_GROUPED_TOTAL_CAPACITY_MUTATION,
  UPDATE_AGE_GROUPED_TOTAL_CAPACITY_MUTATION,
  DELETE_AGE_GROUPED_TOTAL_CAPACITY_MUTATION,
  FORCE_DELETE_AGE_GROUPED_TOTAL_CAPACITY_MUTATION,
  ADD_AGE_GROUPED_TOTAL_CAPACITY_AGE_GROUPS_MUTATION,
  REMOVE_AGE_GROUPED_TOTAL_CAPACITY_AGE_GROUPS_MUTATION,
  CREATE_AGE_GROUPED_TOTAL_CAPACITY_AGE_GROUPS_MUTATION,
  DELETE_AGE_GROUPED_TOTAL_CAPACITY_AGE_GROUPS_MUTATION,
  UPDATE_AGE_GROUPED_TOTAL_CAPACITY_AGE_GROUPS_ORDER_MUTATION,
} from './graphql'

const hydrateObjects = (objects: Array<any>) => {
  return objects.map((object) => hydrateEntityObject(model, object))
}

const hydrateObject = (object: any) => {
  return hydrateEntityObject(model, object)
}
const accommodationAgeGroupedTotalCapacityRepository: EntityRepository = {
  getObject: async (id: string | number, includes = {}): Promise<any> => {
    const variables = { id: id, ...includes }
    return graphQLClient.request(SINGLE_AGE_GROUPED_TOTAL_CAPACITY_QUERY, variables).then((data: any) => {
      return hydrateObject(data.ageGroupedTotalCapacity)
    })
  },

  getObjects: async (includes = {}): Promise<any> => {
    return graphQLClient.request(ALL_AGE_GROUPED_TOTAL_CAPACITIES_QUERY, includes).then((data: any) => {
      return hydrateObjects(data.ageGroupedTotalCapacities)
    })
  },

  useObject: (id: string | number, includes = {}, paused = false) => {
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const variables = useMemo(() => ({ id: id, ...includes }), [id])
    const { data, error, mutate } = useSWR(paused ? null : [SINGLE_AGE_GROUPED_TOTAL_CAPACITY_QUERY, variables], { revalidateOnFocus: false })
    return {
      object: !data ? data : hydrateObject(data.ageGroupedTotalCapacity),
      isLoading: !paused && !error && !data,
      isError: error,
      mutate: mutate,
    }
  },

  useObjects: (includes = {}, paused = false) => {
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const variables = useMemo(() => includes, [])
    const { data, error, mutate } = useSWR(paused ? null : [ALL_AGE_GROUPED_TOTAL_CAPACITIES_QUERY, variables])
    return {
      objects: !data ? data : hydrateObjects(data.ageGroupedTotalCapacities),
      isLoading: !paused && !error && !data,
      isError: error,
      mutate: mutate,
    }
  },

  getDependents: (id: string | number, includes: []) => {
    const includeVars: { [x: string]: boolean } = {}
    includes.forEach((include) => {
      includeVars[`with${uppercaseFirst(include)}`] = true
    })
    const variables = { id: id, ...includeVars }
    return graphQLClient.request(AGE_GROUPED_TOTAL_CAPACITIES_DEPENDENTS_QUERY, variables).then((data: any) => {
      return data.groupedDependents
    })
  },

  useDependents: (id: string | number, includes: []) => {
    const includeVars: { [x: string]: boolean } = {}
    includes.forEach((include) => {
      includeVars[`with${uppercaseFirst(include)}`] = true
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const variables = useMemo(() => ({ id: id, ...includeVars }), [id])
    const { data, error, mutate } = useSWR([AGE_GROUPED_TOTAL_CAPACITIES_DEPENDENTS_QUERY, variables], {
      revalidateOnFocus: false,
    })
    return {
      data: !data ? data : data.groupedDependents,
      isLoading: !error && !data,
      isError: error,
      mutate: mutate,
    }
  },

  createObject: async (data: ObjectInterface, includes = {}): Promise<any> => {
    const variables = {
      input: data,
      ...includes,
    }
    return graphQLClient.request(CREATE_AGE_GROUPED_TOTAL_CAPACITY_MUTATION, variables).then((data: any) => {
      return hydrateObject(data.ageGroupedTotalCapacity)
    })
  },

  updateObject: async (data: ObjectInterface, includes = {}): Promise<any> => {
    const variables = {
      input: data,
      ...includes,
    }
    return graphQLClient.request(UPDATE_AGE_GROUPED_TOTAL_CAPACITY_MUTATION, variables).then((data: any) => {
      return hydrateObject(data.ageGroupedTotalCapacity)
    })
  },

  deleteObject: async (id: string | number): Promise<any> => {
    const variables = {
      input: { id: id },
    }
    return graphQLClient.request(DELETE_AGE_GROUPED_TOTAL_CAPACITY_MUTATION, variables).then((data: any) => {
      return data.deleteAccommodationAgeGroupedTotalCapacity
    })
  },

  forceDeleteObject: async (id: string | number): Promise<any> => {
    const variables = {
      input: { id: id },
    }
    return graphQLClient.request(FORCE_DELETE_AGE_GROUPED_TOTAL_CAPACITY_MUTATION, variables).then((data: any) => {
      return data.forceDeleteAccommodationAgeGroupedTotalCapacity
    })
  },

  assignAgeGroups: async (id: string, itemIds: Array<string>): Promise<any> => {
    const variables = {
      input: {
        id: id,
        ageGroupIds: itemIds,
      },
    }
    return graphQLClient.request(ADD_AGE_GROUPED_TOTAL_CAPACITY_AGE_GROUPS_MUTATION, variables)
  },

  unassignAgeGroups: async (id: string, itemIds: Array<string>): Promise<any> => {
    const variables = {
      input: {
        id: id,
        ageGroupIds: itemIds,
      },
    }
    return graphQLClient.request(REMOVE_AGE_GROUPED_TOTAL_CAPACITY_AGE_GROUPS_MUTATION, variables)
  },

  createAgeGroups: async (id: string, items: Array<travelPricingAgeGroupObject>): Promise<any> => {
    const variables = {
      input: {
        id: id,
        ageGroups: items,
      },
    }
    return graphQLClient.request(CREATE_AGE_GROUPED_TOTAL_CAPACITY_AGE_GROUPS_MUTATION, variables)
  },

  deleteAgeGroups: async (id: string, itemIds: Array<string>): Promise<any> => {
    const variables = {
      input: {
        id: id,
        ageGroupIds: itemIds,
      },
    }
    return graphQLClient.request(DELETE_AGE_GROUPED_TOTAL_CAPACITY_AGE_GROUPS_MUTATION, variables)
  },

  updateAgeGroupsOrder: async (id: string, itemIds: Array<string>): Promise<any> => {
    const variables = {
      input: {
        id: id,
        ageGroupIds: itemIds,
      },
    }
    return graphQLClient.request(UPDATE_AGE_GROUPED_TOTAL_CAPACITY_AGE_GROUPS_ORDER_MUTATION, variables)
  },
}

export default accommodationAgeGroupedTotalCapacityRepository
